/**
 * Month selection for the Marketing report.
 *
 * Every figure on this report is a whole IST calendar month - Query is leads
 * created in the month, Converted is leads won in it, and the cost gate needs
 * both cost inputs per month. A day-level range therefore only ever gets
 * rounded out to the months it touches, which is why this page uses its own
 * month picker instead of the shared `DateRangeFilter`: "Last 90 days" quietly
 * meaning "three whole months, all of which need cost data" was a trap.
 *
 * The selection is a SET, not a span (`?months=2026-05,2026-07`), so an
 * operator can compare July against May without dragging June's spend into the
 * totals. `from`/`to` are still derived and sent, because they bound the span
 * the API prunes on; the month list is what actually filters.
 */

const MONTH_KEY = /^\d{4}-(0[1-9]|1[0-2])$/;

/** Widest selection the API will accept - mirrors `MAX_RANGE_MONTHS` there. */
export const MAX_MONTHS = 36;

/** How many months a fresh visit selects, newest-first from the current month.
 *  Defaults to the current month only; the operator can widen it in the picker. */
const DEFAULT_MONTH_COUNT = 1;

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

export const MONTH_NAMES_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

const pad = (value: number): string => String(value).padStart(2, '0');

/** Split a `YYYY-MM` key into its numeric parts. */
const parts = (month: string): { year: number; index: number } => {
  const [year, index] = month.split('-').map(Number);
  return { year: year!, index: index! };
};

export const monthKey = (year: number, monthIndex: number): string =>
  `${year}-${pad(monthIndex + 1)}`;

/**
 * The current month as observed in Asia/Kolkata. The server groups every
 * aggregate in IST, so a browser in another timezone must not offer (or
 * default to) a month the report has no concept of yet.
 */
const IST_MONTH_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Kolkata',
  year: 'numeric',
  month: '2-digit',
});

export const istCurrentMonth = (): string => {
  // 'en-CA' gives `YYYY-MM` with a stable separator in every browser.
  const [year, month] = IST_MONTH_FORMATTER.format(new Date()).split('-');
  return `${year}-${month}`;
};

/** Shift a month key by `delta` months (negative goes back). */
export const addMonths = (month: string, delta: number): string => {
  const { year, index } = parts(month);
  const shifted = new Date(Date.UTC(year, index - 1 + delta, 1));
  return monthKey(shifted.getUTCFullYear(), shifted.getUTCMonth());
};

/** `2026-07` -> "July 2026". */
export const monthLabel = (month: string): string => {
  const { year, index } = parts(month);
  return `${MONTH_NAMES[index - 1] ?? index} ${year}`;
};

/** `2026-07` -> "Jul 2026". */
export const shortMonthLabel = (month: string): string => {
  const { year, index } = parts(month);
  return `${MONTH_NAMES_SHORT[index - 1] ?? index} ${year}`;
};

/** First IST calendar day of the month, as the API's `from`. */
export const monthStartDate = (month: string): string => `${month}-01`;

/** Last IST calendar day of the month, as the API's `to`. */
export const monthEndDate = (month: string): string => {
  const { year, index } = parts(month);
  // Day 0 of the NEXT month is the last day of this one.
  return `${month}-${pad(new Date(Date.UTC(year, index, 0)).getUTCDate())}`;
};

/** The `n` months ending at (and including) the current IST month. */
export const recentMonths = (count: number, endingAt = istCurrentMonth()): string[] =>
  Array.from({ length: count }, (_, offset) => addMonths(endingAt, offset - (count - 1)));

export const defaultMonths = (): string[] => recentMonths(DEFAULT_MONTH_COUNT);

/**
 * Read the `months` URL param. Unknown / malformed entries are dropped rather
 * than failing the page, and an empty result falls back to the default so the
 * report is never rendered against nothing.
 */
export const parseMonthsParam = (raw: string | null): string[] => {
  if (!raw) return defaultMonths();
  const parsed = [...new Set(raw.split(',').map((part) => part.trim()))]
    .filter((part) => MONTH_KEY.test(part))
    .sort()
    .slice(-MAX_MONTHS);
  return parsed.length > 0 ? parsed : defaultMonths();
};

/** Serialise for the URL / API - always oldest-first so keys stay stable. */
export const serialiseMonths = (months: string[]): string => [...months].sort().join(',');

/** True when the selection is every month between its own ends. */
export const isContiguous = (months: string[]): boolean => {
  if (months.length < 2) return true;
  return months.every((month, index) => index === 0 || month === addMonths(months[index - 1]!, 1));
};

/**
 * Trigger label. A contiguous run reads as a span; a selection with holes can't
 * honestly be written as one, so it falls back to a count the operator can open
 * the picker to inspect.
 */
export const describeMonths = (months: string[]): string => {
  if (months.length === 0) return 'Select months';
  if (months.length === 1) return monthLabel(months[0]!);
  if (isContiguous(months)) {
    return `${shortMonthLabel(months[0]!)} - ${shortMonthLabel(months[months.length - 1]!)}`;
  }
  return `${months.length} months selected`;
};
